import { Controller } from "@hotwired/stimulus";
import { httpRequest } from "../http";
import debounce from "debounce";
const DEBOUNCE_DELAY_MS = 1000;

export default class extends Controller {
  static values = {
    deleteUrl: String,
    loadBodyUrl: String,
  };

  static targets = [
    "validIcon",
    "spinnerIcon",
    "statusComplete",
    "statusIncomplete",
    "bodyContainer",
    "body",
    "collapseIcon",
    "expandIcon",
  ];

  isBodyLoading = false;

  connect() {
    this.element.addEventListener("input", this._handleChange);
  }

  disconnect() {
    this.element.removeEventListener("input", this._handleChange);
  }

  toggleBody() {
    const isCollapsed = this.element.classList.contains("-collapsed");

    if (!this.isBodyLoading && !this.hasBodyTarget) {
      this._loadBody();
    }

    if (isCollapsed) {
      this.element.classList.remove("-collapsed");
      this.collapseIconTarget.classList.remove("hidden");
      this.expandIconTarget.classList.add("hidden");
    } else {
      this.element.classList.add("-collapsed");
      this.collapseIconTarget.classList.add("hidden");
      this.expandIconTarget.classList.remove("hidden");
    }
  }

  delete() {
    this._setLoadingState(true);
    this._debouncedSave.clear();

    httpRequest(this.deleteUrlValue, { method: "DELETE" })
      .then(() => this.dispatch("entryDeleted"))
      .then(() => this.element.remove())
      .catch(() => {
        this._setLoadingState(false);
        alert("There was a problem deleting the entry");
      });
  }

  _loadBody() {
    this.isBodyLoading = true;

    httpRequest(this.loadBodyUrlValue)
      .then((response) => response.text())
      .then((html) => {
        this.bodyContainerTarget.innerHTML = html;
      })
      .catch(() => alert("There was a problem loading the entry details"))
      .finally(() => (this.isBodyLoading = false));
  }

  _handleChange = () => {
    this._setLoadingState(true);
    this._debouncedSave();
  };

  _debouncedSave = debounce(() => this._save(), DEBOUNCE_DELAY_MS);

  _save() {
    httpRequest(this.element.action, {
      method: "PATCH",
      body: new FormData(this.element),
    })
      .then((response) => response.json())
      .then((data) => {
        this._setCompletionStatus(data.status === "complete");

        // Dispatch field-specific errors if any
        if (data.errors) {
          Object.entries(data.errors).forEach(([field, messages]) => {
            const event = new CustomEvent("totalHoursValidation:serverError", {
              detail: { field, message: messages[0] },
              bubbles: true,
            });
            this.element.dispatchEvent(event);
          });
        }
      })
      .then(() => this.dispatch("entryUpdated"))
      .catch(() => alert("There was a problem saving the entry"))
      .finally(() => this._setLoadingState(false));
  }

  _setCompletionStatus(isComplete) {
    if (isComplete) {
      this.statusCompleteTarget.classList.remove("hidden");
      this.statusIncompleteTarget.classList.add("hidden");
      this.element.removeAttribute("data-payroll-entry-incomplete");
    } else {
      this.statusCompleteTarget.classList.add("hidden");
      this.statusIncompleteTarget.classList.remove("hidden");
      this.element.setAttribute("data-payroll-entry-incomplete", "true");
    }
  }

  _setLoadingState(isLoading) {
    if (isLoading) {
      this.spinnerIconTarget.classList.remove("hidden");
      this.validIconTarget.classList.add("hidden");
    } else {
      this.spinnerIconTarget.classList.add("hidden");
      this.validIconTarget.classList.remove("hidden");
    }
  }
}
